/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-09-18 00:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Autocomplete, TextContainer, InlineError } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import DraggableList from './draggable-list';
import { RenderLabelTooltip } from '../forms/render-label-tooltip';

import * as FilterDataActions from 'actions/filter-data-actions';

// The number of item for first loading
const FIRST_LOADING_COUNT = 500;
// The number of next page
const MAX_NUMBER_ITEMS_API = 30;
// Maximum number of items that system can support
const MAX_INDEX_AGGREGATE = 10000;

type Props = {
  label: string,
  suggestionTitle: string,
  placeholder: string,
  helpText: string
};

type ComposedProps = Props & WithI18nProps;

class RenderAutocompleteV2 extends Component<ComposedProps> {
  timerTyping = 0;
  timerTypingPrefix = 0;
  paginationInterval = 25;
  timeoutTextChange = undefined;

  state = {
    loading: false,
    skip: 0, // Index for pagination
    skipOfSearch: 0, // Index for pagination of searching text
    options: [],
    originalOptions: [], // Contain all loaded options (except searching options)
    selectedOptions: [],
    displaySelectedOptions: [],
    inputValue: '',
    blockSearchMore: false
  };

  componentDidMount() {
    const { type, filterPrefix = '', getFilterDataByType } = this.props;

    // Get filter data from API
    getFilterDataByType(type, {
      skip: 0,
      limit: FIRST_LOADING_COUNT,
      keyword: '',
      prefix: filterPrefix ? encodeURIComponent(filterPrefix) : ''
    });
  }

  componentWillReceiveProps(nextProps) {
    const { filterPrefix = '', type = '' } = this.props;
    const { filterPrefix: filterPrefixNext = '', type: typeNext = '' } = nextProps;

    if (type !== typeNext) {
      this.handleReceiveType(typeNext);
    }

    // Read items based on prefix
    if (filterPrefix !== filterPrefixNext) {
      this.handleReceivePrefix(filterPrefixNext);
      return;
    }

    this.handleReceiveFilterData(nextProps);
  }

  handleReceiveType = typeNext => {
    const { filterPrefix = '', getFilterDataByType } = this.props;

    this.setState({ options: [], skip: 0, skipOfSearch: 0, blockSearchMore: false });

    getFilterDataByType(typeNext, {
      skip: 0,
      limit: FIRST_LOADING_COUNT,
      keyword: '',
      prefix: filterPrefix ? encodeURIComponent(filterPrefix) : ''
    });
  };

  // Load filter data based on prefix
  handleReceivePrefix = filterPrefixNext => {
    const { type, getFilterDataByType } = this.props;
    let { inputValue } = this.state;

    // Clear previous waiting process
    clearTimeout(this.timerTypingPrefix);

    this.timerTypingPrefix = setTimeout(() => {
      this.setState({ options: [], skip: 0, blockSearchMore: false });
      getFilterDataByType(type, {
        skip: 0,
        limit: FIRST_LOADING_COUNT,
        keyword: encodeURIComponent(inputValue),
        prefix: filterPrefixNext ? encodeURIComponent(filterPrefixNext) : ''
      });
    }, 300);
  };

  // Save loaded data for rendering
  handleReceiveFilterData = nextProps => {
    const { loadingDataByType } = this.props;
    const { loadingDataByType: loadingDataByTypeNext, filterDataByType = [] } = nextProps;
    let { options, inputValue } = this.state;

    let state = {};

    // Get option value and save as selected options
    const { input = {} } = nextProps;
    const value = _.get(input, 'value', []);
    const selectedOptions = _.isArray(value) ? value.map(option => option.value) : [];
    state.selectedOptions = selectedOptions;
    state.displaySelectedOptions = _.isArray(value) ? _.cloneDeep(value) : [];

    // Save data for rendering
    if (loadingDataByType && !loadingDataByTypeNext) {
      // Remove excluded values
      this.removeExcludedValues(filterDataByType);

      // Append loaded options to the list
      options = _.concat(options, filterDataByType);
      state.options = options;

      // Update original options if loading item without searching
      if (inputValue === '') {
        state.originalOptions = options;
      }

      state.loading = false;

      // Stop load more page if empty data
      if (_.isEmpty(filterDataByType)) {
        state.blockSearchMore = true;
      }
    }

    this.setState(state);
  };

  // Remove excluded values in case of unique selection
  removeExcludedValues = list => {
    const { excludedValues = [] } = this.props;
    if (_.isEmpty(excludedValues)) return;

    excludedValues.forEach(item => {
      _.remove(list, listItem => {
        return item.value === listItem.value;
      });
    });
  };

  componentWillUnmount() {
    // Reset states
    this.setState({
      loading: false,
      skip: 0,
      skipOfSearch: 0,
      options: [],
      originalOptions: [],
      selectedOptions: [],
      displaySelectedOptions: [],
      inputValue: '',
      blockSearchMore: false
    });
  }

  render() {
    const { options, selectedOptions, displaySelectedOptions, loading } = this.state;
    const { label, suggestionTitle, placeholder, textHelper, meta, tooltip } = this.props;

    const textField = (
      <div onKeyDown={this.handleKeyDown} onKeyUp={this.handleKeyUp}>
        <Autocomplete.TextField
          onChange={this.onTextChange}
          label={label ? <RenderLabelTooltip label={label} tooltip={tooltip} /> : undefined}
          value={this.state.inputValue}
          placeholder={placeholder}
        />
      </div>
    );

    return (
      <div>
        <Autocomplete
          allowMultiple
          options={options}
          selected={selectedOptions}
          textField={textField}
          onSelect={this.onSelect}
          listTitle={suggestionTitle}
          onLoadMoreResults={this.onLoadMoreResults}
          preferredPosition="above"
          willLoadMoreResults={true}
          loading={loading}
        />

        {selectedOptions.length > 0 ? (
          <div className="BC-Autocomplete__TextContainer">
            <TextContainer>
              <div>
                <DraggableList
                  list={displaySelectedOptions}
                  onSortEnd={this.onSortEnd}
                  onRemove={this.onRemoveTag}
                />
              </div>
            </TextContainer>
          </div>
        ) : (
          ''
        )}

        <div className="Polaris-Labelled__HelpText">{textHelper}</div>
        {meta.error ? <InlineError message={meta.error} /> : ''}
      </div>
    );
  }

  onSortEnd = result => {
    const displaySelectedOptions = this.reorder(
      this.state.displaySelectedOptions,
      result.oldIndex,
      result.newIndex
    );

    this.setState({
      displaySelectedOptions
    });

    // Call update change for redux form
    this.props.input.onChange(displaySelectedOptions);
  };

  // a little function to help us with reordering the result
  reorder(list, startIndex, endIndex) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  }

  onRemoveTag = tag => {
    const { selectedOptions, displaySelectedOptions } = this.state;

    // Remove from display selected options
    _.remove(displaySelectedOptions, item => {
      return item.value === tag.value;
    });

    _.remove(selectedOptions, item => {
      return item === tag.value;
    });

    this.setState({ selectedOptions, displaySelectedOptions });

    // Call update change for redux form
    this.props.input.onChange(displaySelectedOptions);
  };

  // === Searching ===
  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    const keyCode = event.keyCode;
    // console.log('keyCode', keyCode, ' - key', key);

    const excludedKeycode = [
      37, // arrowleft
      38, // arrowup
      39, // arrowright
      40 // arrowleft
    ];

    if (_.includes(excludedKeycode, keyCode)) {
      return;
    }

    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.startSearching();
    }, 300);
  };

  onTextChange = value => {
    this.setState({ inputValue: value });
  };

  startSearching = () => {
    const { type, filterPrefix = '', getFilterDataByType } = this.props;
    const { originalOptions, inputValue } = this.state;

    // Show loading icon before updating
    this.setState({ loading: true, options: [], inputValue });

    // clearTimeout(this.timeoutTextChange);

    // Using timeout for reinitialize component, avoid to display cached values
    // this.timeoutTextChange = setTimeout(() => {
    // Re-initialize option list
    if (inputValue === '') {
      this.setState({
        options: originalOptions,
        skipOfSearch: 0,
        blockSearchMore: false,
        loading: false
      });
      return;
    }

    // Call API for searching options
    getFilterDataByType(type, {
      skip: 0,
      limit: FIRST_LOADING_COUNT,
      keyword: encodeURIComponent(inputValue),
      prefix: filterPrefix ? encodeURIComponent(filterPrefix) : ''
    });

    this.setState({ skipOfSearch: 0, loading: false, blockSearchMore: false });
    // }, 300);
  };
  // === End Searching ===

  onSelect = items => {
    let { selectedOptions } = this.state;

    // Add new item
    if (selectedOptions.length < items.length) {
      this.addNewItem(items);
    } else {
      // Remove item
      this.removeItem(items);
    }
  };

  addNewItem = items => {
    let { options, selectedOptions = [], displaySelectedOptions = [] } = this.state;

    // Find new item
    const newItems = _.difference(items, selectedOptions);

    // Push new item to saved list
    newItems.forEach(selectedItem => {
      const matchedOption = _.find(options, { value: selectedItem });

      if (matchedOption) {
        selectedOptions.push(selectedItem);
        // Remove unused values if needed
        displaySelectedOptions.push(_.omit(matchedOption, ['id', 'active']));
      }
    });

    this.setState({ selectedOptions, displaySelectedOptions });

    // Call update change for redux form
    this.props.input.onChange(displaySelectedOptions);
  };

  removeItem = items => {
    let { selectedOptions, displaySelectedOptions } = this.state;

    const removedItems = _.difference(selectedOptions, items);

    removedItems.forEach(removedItem => {
      // Remove from selected options
      _.remove(selectedOptions, item => {
        return item === removedItem;
      });

      // Remove from display selected options
      _.remove(displaySelectedOptions, item => {
        return item.value === removedItem;
      });
    });

    this.setState({ selectedOptions, displaySelectedOptions });

    // Call update change for redux form
    this.props.input.onChange(displaySelectedOptions);
  };

  // === Handle load more result ===
  onLoadMoreResults = () => {
    const { loadingDataByType } = this.props;
    let { blockSearchMore, inputValue } = this.state;

    if (blockSearchMore) {
      return;
    }

    // Avoid calling multiple time
    if (loadingDataByType) {
      return;
    }

    // Filter option list by input text
    if (inputValue) {
      this.nextPageWithFilter();
    } else {
      // Call API for getting more items
      this.nextPage();
    }
  };

  // Get next page
  nextPage = () => {
    const { type, filterPrefix = '', getFilterDataByType } = this.props;
    let { skip } = this.state;

    skip += skip ? MAX_NUMBER_ITEMS_API : FIRST_LOADING_COUNT;

    // The index of next pagination is inside the limitation of query
    // Else => Skip
    if (skip < MAX_INDEX_AGGREGATE) {
      const options = {
        skip,
        limit: MAX_NUMBER_ITEMS_API,
        keyword: '',
        prefix: filterPrefix ? encodeURIComponent(filterPrefix) : ''
      };
      getFilterDataByType(type, options);

      this.setState({ skip, loading: true });
    }
  };

  // Get next page if having search text
  nextPageWithFilter = () => {
    const { type, filterPrefix = '', getFilterDataByType } = this.props;
    let { inputValue, skipOfSearch = 0 } = this.state;

    skipOfSearch += skipOfSearch ? MAX_NUMBER_ITEMS_API : FIRST_LOADING_COUNT;
    getFilterDataByType(type, {
      skip: skipOfSearch,
      limit: MAX_NUMBER_ITEMS_API,
      keyword: encodeURIComponent(inputValue),
      prefix: filterPrefix ? encodeURIComponent(filterPrefix) : ''
    });

    this.setState({ skipOfSearch, loading: true });
  };

  // === End Handle load more result ===
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterDataByType: (type, options) =>
    dispatch(FilterDataActions.getFilterDataByType(type, options, ownProps.i18n))
});

const mapStateToProps = (state, props) => ({
  loadingDataByType: _.get(state.filterData, 'loadingDataByType', false),
  filterDataByType: _.get(state.filterData, 'filterDataByType', [])
});

// export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(RenderAutocompleteV2));
export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(RenderAutocompleteV2));
